import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { http } from "../../api/client";

const API_URL = process.env.REACT_APP_API_BASE_URL;

// --- Existing Login Thunk ---
export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await http.post(`${API_URL}/auth/login`, credentials);
      const { access_token, mustChangePassword } = res.data;

      localStorage.setItem("accessToken", access_token);
      // Temporary password: AppRoutes keeps the user on /set-password until changed
      if (mustChangePassword) localStorage.setItem("mustChangePassword", "1");
      else localStorage.removeItem("mustChangePassword");

      return {
        accessToken: access_token,
        mustChangePassword: !!mustChangePassword,
      };
    } catch (err) {
      const msg = err.response?.data?.message || "Login failed";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  },
);

// --- Existing Get Profile Thunk ---
export const getProfile = createAsyncThunk(
  "auth/getProfile",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { accessToken } = getState().auth;

      const res = await http.get(`${API_URL}/auth/profile`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const user = res.data;

      localStorage.setItem("user", JSON.stringify(user));
      // Accounts with no company (super admin) work in the company they picked
      localStorage.setItem("noCompany", user.companyId ? "" : "1");
      localStorage.setItem(
        "companyId",
        user.companyId || localStorage.getItem("workingCompanyId") || "",
      );
      localStorage.setItem(
        "companyName",
        user.companyId
          ? user.companyName || ""
          : localStorage.getItem("workingCompanyName") || "",
      );
      localStorage.setItem("branchId", user.branchId || "");
      localStorage.setItem("branchName", user.branchName || "");
      localStorage.setItem("roleId", user.roleId || "");
      localStorage.setItem("role", user.role || "employee");
      localStorage.setItem("departmentId", user.departmentId || "");
      localStorage.setItem("departmentName", user.departmentName || "");

      return user;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to fetch profile";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  },
);

/**
 * 🔐 1. CHANGE PASSWORD THUNK (logged-in user changes their own password)
 * Expects: { oldPassword, newPassword }
 * Hits: PATCH /auth/change-password
 * 409 = current password is wrong; 400 = new password same as the current one
 */
export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async ({ oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const res = await http.patch(`${API_URL}/auth/change-password`, {
        oldPassword,
        newPassword,
      });
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update password";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  },
);

/**
 * 🔓 2. FORGOT PASSWORD RESET THUNK (Unauthenticated Flow)
 * 🔧 Updated to handle the secure email + hiringDate verification check
 * Expects parameters payload object: { email, hiringDate, newPassword }
 * Hits: PATCH /users/forgot-password/reset
 */
export const forgotPasswordReset = createAsyncThunk(
  "auth/forgotPasswordReset",
  async ({ email, hiringDate, newPassword }, { rejectWithValue }) => {
    try {
      // 🚀 Hits the clean public endpoint with the updated secure validation model
      const res = await http.patch(`${API_URL}/users/forgot-password/reset`, {
        email,
        hiringDate, // Format passed down will be "YYYY-MM-DD" matching backend columns
        newPassword,
      });

      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message || "Password reset failed";
      return rejectWithValue(Array.isArray(msg) ? msg.join(", ") : msg);
    }
  },
);

const getStorageItem = (key) => {
  const item = localStorage.getItem(key);
  return item === "null" || item === "" || item === undefined ? null : item;
};

const initialState = {
  accessToken: getStorageItem("accessToken"),
  mustChangePassword: getStorageItem("mustChangePassword") === "1",
  user: JSON.parse(localStorage.getItem("user") || "null"),
  companyId: getStorageItem("companyId"),
  companyName: getStorageItem("companyName"),
  noCompany: getStorageItem("noCompany") === "1",
  branchId: getStorageItem("branchId"),
  branchName: getStorageItem("branchName"),
  roleId: getStorageItem("roleId"),
  role: getStorageItem("role"),
  departmentId: getStorageItem("departmentId"),
  departmentName: getStorageItem("departmentName"),
  status: "idle",
  passwordStatus: "idle",
  resetPasswordStatus: "idle",
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      Object.assign(state, {
        accessToken: null,
        mustChangePassword: false,
        user: null,
        companyId: null,
        companyName: null,
        noCompany: false,
        branchId: null,
        branchName: null,
        roleId: null,
        role: null,
        departmentId: null,
        departmentName: null,
        status: "idle",
        passwordStatus: "idle",
        resetPasswordStatus: "idle",
        error: null,
      });
      // Clear the session but keep each user's remembered view (hris.view.<userId>)
      const keep = Object.keys(localStorage)
        .filter((k) => k.startsWith("hris.view."))
        .map((k) => [k, localStorage.getItem(k)]);
      localStorage.clear();
      keep.forEach(([k, v]) => localStorage.setItem(k, v));
    },
    /** Accounts with no company (super admin): the company to work in. */
    setWorkingCompany: (state, action) => {
      if (!state.noCompany) return;
      const { id = null, name = null } = action.payload || {};
      state.companyId = id;
      state.companyName = name;
      localStorage.setItem("workingCompanyId", id || "");
      localStorage.setItem("workingCompanyName", name || "");
      localStorage.setItem("companyId", id || "");
      localStorage.setItem("companyName", name || "");
    },
    resetAuthStatuses: (state) => {
      state.passwordStatus = "idle";
      state.resetPasswordStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // --- Login Lifecycle ---
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.accessToken = action.payload.accessToken;
        state.mustChangePassword = !!action.payload.mustChangePassword;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // --- Get Profile Lifecycle ---
      .addCase(getProfile.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload;

        state.noCompany = !action.payload.companyId;
        state.companyId =
          action.payload.companyId || getStorageItem("workingCompanyId");
        state.companyName = action.payload.companyId
          ? action.payload.companyName
          : getStorageItem("workingCompanyName");
        state.branchId = action.payload.branchId;
        state.branchName = action.payload.branchName;
        state.roleId = action.payload.roleId;
        state.role = action.payload.role;
        state.departmentId = action.payload.departmentId;
        state.departmentName = action.payload.departmentName;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // --- 🔐 Change Password Lifecycle ---
      .addCase(changePassword.pending, (state) => {
        state.passwordStatus = "loading";
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.passwordStatus = "succeeded";
        state.mustChangePassword = false;
        localStorage.removeItem("mustChangePassword");
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordStatus = "failed";
        state.error = action.payload;
      })

      // --- 🔓 Forgot Password Reset Lifecycle ---
      .addCase(forgotPasswordReset.pending, (state) => {
        state.resetPasswordStatus = "loading";
        state.error = null;
      })
      .addCase(forgotPasswordReset.fulfilled, (state) => {
        state.resetPasswordStatus = "succeeded";
      })
      .addCase(forgotPasswordReset.rejected, (state, action) => {
        state.resetPasswordStatus = "failed";
        state.error = action.payload;
      });
  },
});

export const { logout, resetAuthStatuses, setWorkingCompany } =
  authSlice.actions;
export default authSlice.reducer;
